import React, { useRef } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Box,
  IconButton,
  Slider,
} from '@mui/material';
import {
  Image as ImageIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { GapStylingConfig } from './types';

interface BackgroundTabProps {
  config: GapStylingConfig;
  onConfigChange: (updates: Partial<GapStylingConfig>) => void;
  onImageUpload: (imageData: string) => void;
}

/**
 * Background tab component for gap styling configuration
 * Handles color, gradient, image, and pattern background settings
 */
const BackgroundTab: React.FC<BackgroundTabProps> = ({
  config,
  onConfigChange,
  onImageUpload
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        onImageUpload(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addGradientColor = () => {
    const newColors = [
      ...config.gradientColors,
      { color: '#000000', position: 50 }
    ];
    onConfigChange({ gradientColors: newColors });
  };

  const removeGradientColor = (index: number) => {
    const newColors = config.gradientColors.filter((_, i) => i !== index);
    onConfigChange({ gradientColors: newColors });
  };

  const updateGradientColor = (index: number, updates: Partial<{ color: string; position: number }>) => {
    const newColors = config.gradientColors.map((color, i) =>
      i === index ? { ...color, ...updates } : color
    );
    onConfigChange({ gradientColors: newColors });
  };

  return (
    <Grid container spacing={2}>
      {config.backgroundType === 'color' && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Background Color"
            type="color"
            value={config.backgroundColor}
            onChange={(e) => onConfigChange({ backgroundColor: e.target.value })}
          />
        </Grid>
      )}

      {config.backgroundType === 'gradient' && (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gradient Type</InputLabel>
              <Select
                value={config.gradientType}
                onChange={(e) => onConfigChange({ gradientType: e.target.value as GapStylingConfig['gradientType'] })}
              >
                <MenuItem value="linear">Linear</MenuItem>
                <MenuItem value="radial">Radial</MenuItem>
                <MenuItem value="conic">Conic</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Direction/Angle"
              value={config.gradientDirection}
              onChange={(e) => onConfigChange({ gradientDirection: e.target.value })}
              placeholder="90deg, to right, etc."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Gradient Colors</Typography>
            {config.gradientColors.map((color, index) => (
              <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                <TextField
                  type="color"
                  value={color.color}
                  onChange={(e) => updateGradientColor(index, { color: e.target.value })}
                />
                <TextField
                  type="number"
                  label="Position %"
                  value={color.position}
                  onChange={(e) => updateGradientColor(index, { position: Number(e.target.value) })}
                  inputProps={{ min: 0, max: 100 }}
                />
                <IconButton onClick={() => removeGradientColor(index)} disabled={config.gradientColors.length <= 2}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addGradientColor}>
              Add Color
            </Button>
          </Grid>
        </>
      )}

      {config.backgroundType === 'image' && (
        <>
          <Grid item xs={12}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Image
            </Button>
            {config.backgroundImage && (
              <Chip
                label="Image Uploaded"
                onDelete={() => onConfigChange({ backgroundImage: '' })}
                color="success"
                sx={{ ml: 1 }}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Image Repeat</InputLabel>
              <Select
                value={config.imageRepeat}
                onChange={(e) => onConfigChange({ imageRepeat: e.target.value as GapStylingConfig['imageRepeat'] })}
              >
                <MenuItem value="no-repeat">No Repeat</MenuItem>
                <MenuItem value="repeat">Repeat</MenuItem>
                <MenuItem value="repeat-x">Repeat X</MenuItem>
                <MenuItem value="repeat-y">Repeat Y</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Image Size</InputLabel>
              <Select
                value={config.imageSize}
                onChange={(e) => onConfigChange({ imageSize: e.target.value as GapStylingConfig['imageSize'] })}
              >
                <MenuItem value="auto">Auto</MenuItem>
                <MenuItem value="cover">Cover</MenuItem>
                <MenuItem value="contain">Contain</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {config.imageSize === 'custom' && (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Custom Width"
                  value={config.imageSizeWidth}
                  onChange={(e) => onConfigChange({ imageSizeWidth: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Custom Height"
                  value={config.imageSizeHeight}
                  onChange={(e) => onConfigChange({ imageSizeHeight: e.target.value })}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image Position"
              value={config.imagePosition}
              onChange={(e) => onConfigChange({ imagePosition: e.target.value })}
              placeholder="center, top left, 50% 50%, etc."
            />
          </Grid>
        </>
      )}

      {config.backgroundType === 'pattern' && (
        <>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Pattern Type</InputLabel>
              <Select
                value={config.patternType}
                onChange={(e) => onConfigChange({ patternType: e.target.value as GapStylingConfig['patternType'] })}
              >
                <MenuItem value="dots">Dots</MenuItem>
                <MenuItem value="stripes">Stripes</MenuItem>
                <MenuItem value="waves">Waves</MenuItem>
                <MenuItem value="checkerboard">Checkerboard</MenuItem>
                <MenuItem value="zigzag">Zigzag</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              type="color"
              label="Primary Color"
              value={config.patternColor}
              onChange={(e) => onConfigChange({ patternColor: e.target.value })}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              type="color"
              label="Secondary Color"
              value={config.patternSecondaryColor}
              onChange={(e) => onConfigChange({ patternSecondaryColor: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>Pattern Size</Typography>
            <Slider
              value={config.patternSize}
              onChange={(_, value) => onConfigChange({ patternSize: value as number })}
              min={5}
              max={50}
              valueLabelDisplay="auto"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default BackgroundTab;